import { useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { MousePointerClick, Plus } from "lucide-react";
import { sectionRegistry, type SectionType } from "@sections/registry";
import { useRenderContext } from "@sections/RenderContext";
import type { EditorState } from "./useEditorState";
import { SECTION_LABELS } from "./constants";
import { TextHintCard } from "./inspector/TextHintCard";
import { AlignmentControl } from "./inspector/AlignmentControl";
import { BackgroundSwatches } from "./inspector/BackgroundSwatches";
import { CardStyleControl } from "./inspector/CardStyleControl";
import { ImageShapeControl } from "./inspector/ImageShapeControl";
import { OverlayControl } from "./inspector/OverlayControl";
import { ImageUploadField } from "./inspector/ImageUploadField";
import { getCropConstraint } from "./inspector/cropConstraints";
import { LinkField } from "./inspector/LinkField";
import { SectionActions } from "./inspector/SectionActions";
import { LayoutSwitcher } from "./inspector/LayoutSwitcher";
import { SegmentedControl } from "./inspector/SegmentedControl";
import { SectionSettings } from "./inspector/SectionSettings";
import { EmbedUrlField } from "./inspector/EmbedUrlField";
import { ItemListEditor } from "./inspector/ItemListEditor";
import { isControlVisible } from "./inspector/fieldVisibility";
import { SECTION_ITEM_META } from "@sections/itemMeta";

interface InspectorPanelProps {
  editor: EditorState;
  onOpenLibrary: () => void;
}

/** Human-friendly labels for link/href prop names. */
function hrefLabel(propName: string): string {
  if (propName === "ctaHref") return "Button link";
  // Strip trailing Href/Link and add " link"
  return (
    propName
      .replace(/Href$/, "")
      .replace(/Link$/, "")
      .replace(/([A-Z])/g, " $1")
      .trim() + " link"
  );
}

/** Prop names that represent images (show upload fields). */
const IMAGE_PROPS = ["backgroundImageUrl", "imageUrl"] as const;

/**
 * Derives crop metadata prop names from an image prop name.
 * e.g. "imageUrl" -> { originalUrl, originalStorageKey, crop }
 *      "backgroundImageUrl" -> { originalUrl: "backgroundImageOriginalUrl", ... }
 */
function cropMetaPropNames(imagePropName: string) {
  if (imagePropName === "backgroundImageUrl") {
    return {
      originalUrl: "backgroundImageOriginalUrl",
      originalStorageKey: "backgroundImageOriginalStorageKey",
      crop: "backgroundImageCrop",
    };
  }
  // "imageUrl" -> "image"
  const base = imagePropName.replace(/Url$/, "");
  return {
    originalUrl: `${base}OriginalUrl`,
    originalStorageKey: `${base}OriginalStorageKey`,
    crop: `${base}Crop`,
  };
}

/** Returns true if a prop key looks like a link/href field. */
function isHrefProp(key: string): boolean {
  return (
    (key.endsWith("Href") || key.endsWith("Link")) &&
    !key.endsWith("Label")
  );
}

/** Prop names that are text and should NOT appear in the inspector. */
const TEXT_PROPS = new Set([
  "heading",
  "subheading",
  "description",
  "body",
  "ctaLabel",
  "items",
  "questions",
  "title",
  "plans",
  "columns",
  "count",
  "tagFilter",
  "rates",
  "cadence",
  /* Repeatable item arrays -- handled by ItemListEditor */
  "members",
  "stats",
  "steps",
  "logos",
  "cards",
  "rows",
  "columnHeaders",
  /* Embed props -- handled by EmbedUrlField */
  "videoUrl",
  "url",
  "embedUrl",
  "height",
  "allow",
  "address",
  "showAddress",
  "aspectRatio",
  /* Global section props -- handled by dedicated controls */
  "density",
  "width",
  "headingLevel",
  "topDivider",
  "bottomDivider",
  "hideOnMobile",
  /* Phase 2 section controls -- handled by dedicated controls */
  "cardStyle",
  "imageShape",
  "photoShape",
  "ctaTarget",
  "overlayOpacity",
  "backgroundPosition",
  "imageAlt",
  /* Phase 3 section controls -- handled by dedicated controls */
  "showDate",
  "highlightIndex",
  "includedPlanIds",
  "pinnedPostIds",
  "includedFaqIds",
  "maxItems",
  "featuredCount",
  "listCount",
  /* Phase 4 section controls -- handled by dedicated controls */
  "iconSize",
  "textAlign",
  "showAuthor",
  "readMoreLabel",
  "showRating",
  "showCta",
  "ctaStyle",
  "ctaColor",
  "secondaryCtaColor",
  "imageAspect",
  /* TextBlock extras -- handled by dedicated controls */
  "dividerStyle",
  "intro",
  "outro",
  "columnCardStyle",
  /* CTABanner microcopy -- handled by dedicated control */
  "microcopy",
  /* PricingTable footnote + plan blurbs -- handled by dedicated controls */
  "footnote",
  "planBlurbs",
  /* Crop metadata -- managed by ImageUploadField internally */
  "imageOriginalUrl",
  "imageOriginalStorageKey",
  "imageCrop",
  "backgroundImageOriginalUrl",
  "backgroundImageOriginalStorageKey",
  "backgroundImageCrop",
  /* Secondary CTA props -- handled by dedicated controls */
  "secondaryCtaLabel",
  "secondaryCtaHref",
  "secondaryCtaStyle",
  "secondaryCtaTarget",
  /* Billing display -- handled by dedicated control */
  "billingDisplay",
  /* FAQ search -- handled by dedicated control */
  "showSearch",
]);

/** Sections whose width control should be hidden (fixed container widths). */
const WIDTH_HIDDEN_SECTIONS = new Set(["VideoEmbed", "BookingWidget", "ComparisonTable"]);
/** Sections whose heading level control should be hidden (non-semantic headings). */
const HEADING_HIDDEN_SECTIONS = new Set(["LogoStrip"]);
/** Sections whose background image upload should be hidden (embed/data sections and bare-mode sections). */
const BG_IMAGE_EXCLUDED_SECTIONS = new Set(["VideoEmbed", "MapEmbed", "CustomEmbed", "ComparisonTable", "TextBlock", "ProcessSteps", "CTABanner", "BookingWidget"]);

/** Guidance messages for data-driven sections that pull content from elsewhere. */
const DATA_SOURCE_GUIDANCE: Record<string, ReactNode> = {
  FAQ: (
    <>
      FAQ content is managed in{" "}
      <Link to="/website/faqs" className="text-tide underline">
        Website &rsaquo; FAQs
      </Link>
      . This section displays your published FAQs.
    </>
  ),
  PricingTable: (
    <>
      Pricing data is pulled from your catalog. Manage services and rates in{" "}
      <Link to="/catalog" className="text-tide underline">
        Settings &rsaquo; Catalog
      </Link>
      .
    </>
  ),
};

/** Extracted component for the PricingTable highlight-plan dropdown.
 *  Uses useEffect (not queueMicrotask) to reset highlightIndex when it
 *  exceeds the filtered list length. */
function HighlightPlanControl({
  highlightIndex,
  includedPlanIds,
  bookingTypes,
  updateProp,
}: {
  highlightIndex: number | null;
  includedPlanIds: string[] | null;
  bookingTypes: { id: string; name: string }[];
  updateProp: (propName: string, value: unknown) => void;
}) {
  const filteredBookingTypes = includedPlanIds
    ? bookingTypes.filter((bt) => includedPlanIds.includes(bt.id))
    : bookingTypes;

  useEffect(() => {
    if (
      typeof highlightIndex === "number" &&
      highlightIndex >= 0 &&
      highlightIndex >= filteredBookingTypes.length
    ) {
      updateProp("highlightIndex", null);
    }
  }, [highlightIndex, filteredBookingTypes.length, updateProp]);

  return (
    <div className="mb-[18px]">
      <label htmlFor="inspector-highlight-plan" className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        Highlight plan
      </label>
      <select
        id="inspector-highlight-plan"
        value={highlightIndex === null ? "auto" : String(highlightIndex)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "auto") updateProp("highlightIndex", null);
          else updateProp("highlightIndex", Number(v));
        }}
        className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
      >
        <option value="auto">Auto (middle plan)</option>
        <option value="-1">None</option>
        {(filteredBookingTypes.length > 0
          ? filteredBookingTypes
          : [{ id: "1", name: "Plan 1" }, { id: "2", name: "Plan 2" }, { id: "3", name: "Plan 3" }, { id: "4", name: "Plan 4" }, { id: "5", name: "Plan 5" }]
        ).map((bt, i) => (
          <option key={bt.id} value={String(i)}>
            {bt.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function InspectorPanel({ editor, onOpenLibrary }: InspectorPanelProps) {
  const renderContext = useRenderContext();
  const selectedRow = editor.selectedSectionId
    ? editor.content.find((r) => r.id === editor.selectedSectionId) ?? null
    : null;
  const label = selectedRow
    ? SECTION_LABELS[selectedRow.type] || selectedRow.type.toUpperCase()
    : "";

  // Merge the section's schema defaults under the saved props: content saved
  // before a schema gained a field (e.g. backgroundStyle) has no key for it,
  // and control detection below is key-based. Defaults make every supported
  // control appear with its effective value.
  const mod = selectedRow
    ? sectionRegistry[selectedRow.type as SectionType]
    : undefined;
  const props = { ...(mod?.defaults ?? {}), ...(selectedRow?.props ?? {}) } as Record<
    string,
    unknown
  >;
  const sectionId = selectedRow?.id ?? "";
  const isHidden = selectedRow?.enabled === false;

  const updateProp = useCallback(
    (propName: string, value: unknown) => {
      if (!sectionId) return;
      editor.updateSection(sectionId, { [propName]: value });
    },
    [editor, sectionId],
  );

  // Dead-control hiding for global section settings
  const sectionType = selectedRow?.type ?? "";
  const currentLayout = (props.layout as string) ?? "";

  // Detect which controls to show based on prop keys + layout visibility.
  const hasAlignment =
    "alignment" in props &&
    isControlVisible(sectionType, currentLayout, "alignment");
  const hasBackgroundStyle =
    "backgroundStyle" in props &&
    isControlVisible(sectionType, currentLayout, "backgroundStyle");
  const hasCardStyle =
    "cardStyle" in props &&
    isControlVisible(sectionType, currentLayout, "cardStyle");
  const hasImageShape =
    ("imageShape" in props || "photoShape" in props) &&
    isControlVisible(sectionType, currentLayout, "imageShape");
  const imageShapeProp = "photoShape" in props ? "photoShape" : "imageShape";
  const imageShapeOptions: string[] = "photoShape" in props
    ? ["square", "rounded", "circle"]
    : ["square", "rounded"];
  // Detect image-capable props from the section's SCHEMA definition, not the
  // instance props. Optional fields with no default (e.g. backgroundImageUrl)
  // are absent from parsed defaults, so `p in props` misses them on fresh
  // sections. The schema shape always contains every declared field.
  const schemaShape = (() => {
    try {
      const obj = mod?.schema;
      if (!obj) return null;
      // Zod 4: ZodObject exposes .shape as a public property
      return obj.shape && typeof obj.shape === "object" ? obj.shape : null;
    } catch {
      return null;
    }
  })();
  const hasOverlay =
    "overlayOpacity" in props && !!props.backgroundImageUrl && !BG_IMAGE_EXCLUDED_SECTIONS.has(sectionType) &&
    isControlVisible(sectionType, currentLayout, "overlay");
  const hasImageAlt =
    (schemaShape ? "imageAlt" in schemaShape : "imageAlt" in props) &&
    isControlVisible(sectionType, currentLayout, "imageAlt");
  const imageProps = IMAGE_PROPS.filter((p) => {
    if (!(schemaShape ? p in schemaShape : p in props)) return false;
    if (p === "backgroundImageUrl" && BG_IMAGE_EXCLUDED_SECTIONS.has(sectionType)) return false;
    // Gate each image prop by the visibility map
    if (!isControlVisible(sectionType, currentLayout, p)) return false;
    return true;
  });
  const hrefProps = Object.keys(props).filter(
    (k) => isHrefProp(k) && !TEXT_PROPS.has(k) &&
    (k === "ctaHref" ? isControlVisible(sectionType, currentLayout, "ctaHref") : true),
  );
  const showWidth =
    !WIDTH_HIDDEN_SECTIONS.has(sectionType) &&
    isControlVisible(sectionType, currentLayout, "width");
  const showDensity = isControlVisible(sectionType, currentLayout, "density");
  // `props` is merged with the section's schema defaults, so this is true
  // only for sections that actually declare the prop (today: Hero).
  const showTextWidth =
    "textWidth" in props &&
    isControlVisible(sectionType, currentLayout, "textWidth");
  const showHeadingLevel =
    !HEADING_HIDDEN_SECTIONS.has(sectionType) &&
    isControlVisible(sectionType, currentLayout, "headingLevel");
  // Embed sections get a dedicated EmbedUrlField instead of generic controls
  const embedMode: "video" | "map" | "custom" | null =
    sectionType === "VideoEmbed"
      ? "video"
      : sectionType === "MapEmbed"
        ? "map"
        : sectionType === "CustomEmbed"
          ? "custom"
          : null;

  // Repeatable item list metadata (hoisted from JSX for readability)
  const itemMeta = sectionType in SECTION_ITEM_META ? SECTION_ITEM_META[sectionType] : null;
  const itemsArray = itemMeta
    ? Array.isArray(props[itemMeta.items.prop]) ? (props[itemMeta.items.prop] as Record<string, unknown>[]) : []
    : [];
  const colHeaders = itemMeta?.columns && Array.isArray(props[itemMeta.columns.prop])
    ? (props[itemMeta.columns.prop] as string[])
    : undefined;

  return (
    <aside
      className="w-[288px] flex-none overflow-hidden bg-paper-hi"
      style={{
        borderLeft: "1px solid var(--color-ink-6)",
      }}
    >
      <div className="w-[288px] h-full overflow-y-auto px-[18px] pt-[18px] pb-8">
        {selectedRow ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-tide">
                {label}
              </span>
              <button
                onClick={() => editor.selectSection(null)}
                title="Deselect section"
                className="text-ink-5 p-1 rounded-[7px] hover:bg-paper-2 transition-colors"
              >
                <MousePointerClick size={16} strokeWidth={2} />
              </button>
            </div>

            {/* Title */}
            <div className="font-display text-[19px] font-semibold mb-3.5">
              {SECTION_LABELS[selectedRow.type] || selectedRow.type}
            </div>

            {/* a0. Layout variant switcher */}
            <LayoutSwitcher
              sectionType={selectedRow.type}
              currentValue={
                ("layout" in props
                  ? props.layout
                  : "columns" in props
                    ? props.columns
                    : undefined) as string | number
              }
              onChange={(value) => {
                // Determine which prop to update based on what the section uses
                const propName = "layout" in props ? "layout" : "columns";
                // Hero: the legacy `alignment` prop maps centered→left at
                // render time, so an explicit "Centered" pick would be
                // silently overridden by a stored alignment:"left". Keep the
                // two in sync so the switcher choice always takes effect.
                if (
                  selectedRow?.type === "Hero" &&
                  (value === "centered" || value === "left") &&
                  sectionId
                ) {
                  editor.updateSection(sectionId, {
                    layout: value,
                    alignment: value === "left" ? "left" : "center",
                  });
                  return;
                }
                updateProp(propName, value);
              }}
            />

            {/* a0b. Columns control for sections with BOTH layout and columns (e.g. BlogGrid) */}
            {"columns" in props && "layout" in props && props.layout === "grid" && (
              <SegmentedControl
                label="Columns"
                options={[
                  { label: "2 Columns", value: "2" },
                  { label: "3 Columns", value: "3" },
                  { label: "4 Columns", value: "4" },
                ]}
                value={String(props.columns)}
                onChange={(v) => updateProp("columns", Number(v))}
              />
            )}

            {/* a. Text hint */}
            <TextHintCard />

            {/* a2. Data-source guidance for data-driven sections */}
            {selectedRow && DATA_SOURCE_GUIDANCE[selectedRow.type] && (
              <div className="mb-4 rounded-lg bg-tide-ss px-3.5 py-3 text-[12px] text-tide-d leading-relaxed">
                {DATA_SOURCE_GUIDANCE[selectedRow.type]}
              </div>
            )}

            {/* b. Background style */}
            {hasBackgroundStyle && (
              <BackgroundSwatches
                value={(props.backgroundStyle as string) || "primary"}
                onChange={(v) => updateProp("backgroundStyle", v)}
              />
            )}

            {/* b2. Density + Width + Text width (style group) */}
            <SectionSettings
              group="style"
              props={props}
              updateProp={updateProp}
              showDensity={showDensity}
              showWidth={showWidth}
              showTextWidth={showTextWidth}
              sectionType={sectionType}
            />

            {/* b2b. Plans shown (PricingTable) */}
            {"includedPlanIds" in props && renderContext.bookingTypes.length > 1 && (
              <div className="mb-[18px]">
                <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Plans shown
                </span>
                <div className="flex flex-col gap-1.5">
                  {renderContext.bookingTypes.map((bt) => {
                    const ids = props.includedPlanIds as string[] | null;
                    const isChecked = ids === null || ids.includes(bt.id);
                    // Count how many are currently checked
                    const checkedCount = ids === null
                      ? renderContext.bookingTypes.length
                      : ids.length;
                    const isLastChecked = isChecked && checkedCount <= 1;
                    return (
                      <label key={bt.id} className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isLastChecked}
                          title={isLastChecked ? "At least one plan must be shown" : undefined}
                          onChange={() => {
                            const allIds = renderContext.bookingTypes.map((b) => b.id);
                            if (isChecked) {
                              // Uncheck: remove this ID
                              const newIds = ids === null
                                ? allIds.filter((id) => id !== bt.id)
                                : ids.filter((id) => id !== bt.id);
                              updateProp("includedPlanIds", newIds);
                            } else {
                              // Check: add this ID
                              const newIds = [...(ids ?? []), bt.id];
                              // If all plans are now included, set back to null
                              if (allIds.every((id) => newIds.includes(id))) {
                                updateProp("includedPlanIds", null);
                              } else {
                                updateProp("includedPlanIds", newIds);
                              }
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                        <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                          {bt.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* b2c. Plan blurbs (PricingTable) */}
            {"planBlurbs" in props && renderContext.bookingTypes.length > 0 && (() => {
              const ids = props.includedPlanIds as string[] | null;
              const visiblePlans = ids
                ? renderContext.bookingTypes.filter((bt) => ids.includes(bt.id))
                : renderContext.bookingTypes;
              if (visiblePlans.length === 0) return null;
              const blurbs = (props.planBlurbs as Record<string, string>) || {};
              return (
                <div className="mb-[18px]">
                  <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                    Plan descriptions
                  </span>
                  <span className="block text-[11px] text-ink-4 mb-[7px]">
                    Overrides the catalog description on this section
                  </span>
                  <div className="flex flex-col gap-2">
                    {visiblePlans.map((bt) => (
                      <div key={bt.id}>
                        <label
                          htmlFor={`inspector-blurb-${bt.id}`}
                          className="block text-[11px] text-ink-4 mb-1"
                        >
                          {bt.name}
                        </label>
                        <input
                          id={`inspector-blurb-${bt.id}`}
                          type="text"
                          value={blurbs[bt.id] || ""}
                          onChange={(e) => {
                            const next = { ...blurbs };
                            if (e.target.value) {
                              next[bt.id] = e.target.value;
                            } else {
                              delete next[bt.id];
                            }
                            updateProp("planBlurbs", next);
                          }}
                          placeholder="Overrides the catalog description"
                          className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* b3. Highlight plan (PricingTable) */}
            {"highlightIndex" in props && isControlVisible(sectionType, currentLayout, "highlightIndex") && (
              <HighlightPlanControl
                highlightIndex={props.highlightIndex as number | null}
                includedPlanIds={props.includedPlanIds as string[] | null}
                bookingTypes={renderContext.bookingTypes}
                updateProp={updateProp}
              />
            )}

            {/* b4. Pinned posts (BlogGrid) */}
            {"pinnedPostIds" in props && renderContext.blogPosts.length > 0 && (
              <div className="mb-[18px]">
                <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Pinned posts
                </span>
                <div className="flex flex-col gap-1.5">
                  {renderContext.blogPosts.map((post) => {
                    const ids = props.pinnedPostIds as string[] | null;
                    const isChecked = ids !== null && ids.includes(post.id);
                    return (
                      <label key={post.id} className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              // Uncheck: remove this ID
                              const newIds = (ids ?? []).filter((id) => id !== post.id);
                              updateProp("pinnedPostIds", newIds.length > 0 ? newIds : null);
                            } else {
                              // Check: add this ID
                              const newIds = [...(ids ?? []), post.id];
                              updateProp("pinnedPostIds", newIds);
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                        />
                        <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors truncate">
                          {post.title}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {(props.pinnedPostIds as string[] | null) === null && (
                  <p className="mt-1.5 text-[10.5px] text-ink-5">
                    No posts pinned. Check posts above to pin them to the top.
                  </p>
                )}
              </div>
            )}

            {/* b5. Included FAQs (FAQ) */}
            {"includedFaqIds" in props && renderContext.faqItems.length > 1 && (
              <div className="mb-[18px]">
                <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  FAQs shown
                </span>
                <div className="flex flex-col gap-1.5">
                  {renderContext.faqItems.map((faq) => {
                    const ids = props.includedFaqIds as string[] | null;
                    const isChecked = ids === null || ids.includes(faq.id);
                    // Count how many are currently checked
                    const checkedCount = ids === null
                      ? renderContext.faqItems.length
                      : ids.length;
                    const isLastChecked = isChecked && checkedCount <= 1;
                    return (
                      <label key={faq.id} className="flex items-center gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isLastChecked}
                          title={isLastChecked ? "At least one FAQ must be shown" : undefined}
                          onChange={() => {
                            const allIds = renderContext.faqItems.map((f) => f.id);
                            if (isChecked) {
                              // Uncheck: remove this ID
                              const newIds = ids === null
                                ? allIds.filter((id) => id !== faq.id)
                                : ids.filter((id) => id !== faq.id);
                              updateProp("includedFaqIds", newIds);
                            } else {
                              // Check: add this ID
                              const newIds = [...(ids ?? []), faq.id];
                              // If all FAQs are now included, set back to null
                              if (allIds.every((id) => newIds.includes(id))) {
                                updateProp("includedFaqIds", null);
                              } else {
                                updateProp("includedFaqIds", newIds);
                              }
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                        <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors truncate">
                          {faq.question}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* b6. Show search box (FAQ) */}
            {"showSearch" in props && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!props.showSearch}
                    onChange={(e) => updateProp("showSearch", e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show search box
                  </span>
                </label>
              </div>
            )}

            {/* c. Alignment */}
            {hasAlignment && (
              <AlignmentControl
                value={(props.alignment as string) || "center"}
                onChange={(v) => updateProp("alignment", v)}
              />
            )}

            {/* c2. Card style */}
            {hasCardStyle && sectionType === "ProcessSteps" && (
              <SegmentedControl
                label="Card style"
                options={[
                  { label: "Plain", value: "plain" },
                  { label: "Cards", value: "cards" },
                ]}
                value={(props.cardStyle as string) || "plain"}
                onChange={(v) => updateProp("cardStyle", v)}
              />
            )}
            {hasCardStyle && sectionType !== "ProcessSteps" && (
              <CardStyleControl
                value={(props.cardStyle as string) || "elevated"}
                onChange={(v) => updateProp("cardStyle", v)}
              />
            )}

            {/* c2b. Icon size (FeatureGrid) */}
            {"iconSize" in props && (
              <SegmentedControl
                label="Icon size"
                options={[
                  { label: "Small", value: "small" },
                  { label: "Medium", value: "medium" },
                  { label: "Large", value: "large" },
                ]}
                value={(props.iconSize as string) || "medium"}
                onChange={(v) => updateProp("iconSize", v)}
              />
            )}

            {/* c2c. Text alignment (FeatureGrid, CTACards) */}
            {"textAlign" in props && (
              <SegmentedControl
                label="Text alignment"
                options={[
                  { label: "Center", value: "center" },
                  { label: "Left", value: "left" },
                ]}
                value={(props.textAlign as string) || "center"}
                onChange={(v) => updateProp("textAlign", v)}
              />
            )}

            {/* c2d. CTA style */}
            {"ctaStyle" in props && isControlVisible(sectionType, currentLayout, "ctaStyle") && (
              <SegmentedControl
                label="Button style"
                options={[
                  { label: "Solid", value: "solid" },
                  { label: "Outline", value: "outline" },
                  { label: "Ghost", value: "ghost" },
                ]}
                value={(props.ctaStyle as string) || "solid"}
                onChange={(v) => updateProp("ctaStyle", v)}
              />
            )}

            {/* c2d2. CTA button color */}
            {(schemaShape ? "ctaColor" in schemaShape : "ctaColor" in props) && isControlVisible(sectionType, currentLayout, "ctaColor") && (
              <BackgroundSwatches
                label="Button color"
                value={(props.ctaColor as string) || "primary"}
                onChange={(v) => updateProp("ctaColor", v)}
              />
            )}

            {/* c2d3. Secondary CTA group (Hero -- non-banner layouts) */}
            {(schemaShape ? "secondaryCtaLabel" in schemaShape : "secondaryCtaLabel" in props) && props.layout !== "banner" && (
              <>
                <div className="mb-[18px]">
                  <label htmlFor="inspector-secondary-cta-label" className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                    Secondary button label
                  </label>
                  <input
                    id="inspector-secondary-cta-label"
                    type="text"
                    value={(props.secondaryCtaLabel as string) || ""}
                    onChange={(e) => updateProp("secondaryCtaLabel", e.target.value || undefined)}
                    placeholder="e.g. Learn More"
                    className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                  />
                </div>
                <LinkField
                  label="Secondary button link"
                  value={(props.secondaryCtaHref as string) || ""}
                  onChange={(v) => updateProp("secondaryCtaHref", v)}
                  target={(props.secondaryCtaTarget as string) || "_self"}
                  onTargetChange={(v) => updateProp("secondaryCtaTarget", v)}
                />
                <SegmentedControl
                  label="Secondary button style"
                  options={[
                    { label: "Solid", value: "solid" },
                    { label: "Outline", value: "outline" },
                    { label: "Ghost", value: "ghost" },
                  ]}
                  value={(props.secondaryCtaStyle as string) || "outline"}
                  onChange={(v) => updateProp("secondaryCtaStyle", v)}
                />
                <BackgroundSwatches
                  label="Secondary button color"
                  value={(props.secondaryCtaColor as string) || (props.ctaColor as string) || "primary"}
                  onChange={(v) => updateProp("secondaryCtaColor", v)}
                />
              </>
            )}

            {/* c2d4. Billing display (PricingTable) */}
            {(schemaShape ? "billingDisplay" in schemaShape : "billingDisplay" in props) && (
              <div className="mb-[18px]">
                <label htmlFor="inspector-billing-display" className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Price display
                </label>
                <select
                  id="inspector-billing-display"
                  value={(props.billingDisplay as string) || "flat"}
                  onChange={(e) => updateProp("billingDisplay", e.target.value)}
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                >
                  <option value="flat">Flat rate</option>
                  <option value="per-minute">Per minute</option>
                  <option value="per-hour">Per hour</option>
                </select>
              </div>
            )}

            {/* c2a2. Divider style (TextBlock divider layout) */}
            {isControlVisible(sectionType, currentLayout, "dividerStyle") && "dividerStyle" in props && (
              <SegmentedControl
                label="Divider style"
                options={[
                  { label: "Line", value: "line" },
                  { label: "Dots", value: "dots" },
                  { label: "Space", value: "space" },
                ]}
                value={(props.dividerStyle as string) || "line"}
                onChange={(v) => updateProp("dividerStyle", v)}
              />
            )}

            {/* c2e. Image aspect ratio (BlogGrid, ImageText) */}
            {"imageAspect" in props && isControlVisible(sectionType, currentLayout, "imageAspect") && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Image aspect
                </label>
                <select
                  value={(props.imageAspect as string) || "auto"}
                  onChange={(e) => updateProp("imageAspect", e.target.value)}
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                >
                  {/* Show options relevant to the section type */}
                  {(props.imageAspect as string) === "auto" || "auto" === (mod?.defaults as Record<string, unknown>)?.imageAspect ? (
                    <>
                      <option value="auto">Auto</option>
                      <option value="4:3">4:3</option>
                      <option value="16:9">16:9</option>
                      <option value="1:1">Square</option>
                    </>
                  ) : (
                    <>
                      <option value="video">16:9 (Video)</option>
                      <option value="4:3">4:3</option>
                      <option value="1:1">Square</option>
                    </>
                  )}
                </select>
              </div>
            )}

            {/* c3. Image shape */}
            {hasImageShape && (
              <ImageShapeControl
                value={(props[imageShapeProp] as string) || imageShapeOptions[imageShapeOptions.length - 1]}
                onChange={(v) => updateProp(imageShapeProp, v)}
                options={imageShapeOptions}
              />
            )}

            {/* c4. Overlay opacity */}
            {hasOverlay && (
              <OverlayControl
                value={(props.overlayOpacity as string) || "medium"}
                onChange={(v) => updateProp("overlayOpacity", v)}
              />
            )}

            {/* c5. Embed URL fields (VideoEmbed, MapEmbed, CustomEmbed) */}
            {embedMode && (
              <EmbedUrlField
                mode={embedMode}
                props={props}
                updateProp={updateProp}
              />
            )}

            {/* c6. Video aspect ratio (VideoEmbed only) */}
            {"aspectRatio" in props && sectionType === "VideoEmbed" && (
              <SegmentedControl
                label="Aspect ratio"
                options={[
                  { label: "16:9", value: "16:9" },
                  { label: "4:3", value: "4:3" },
                  { label: "1:1", value: "1:1" },
                ]}
                value={(props.aspectRatio as string) || "16:9"}
                onChange={(v) => updateProp("aspectRatio", v)}
              />
            )}

            {/* c7. Repeatable item list editor */}
            {itemMeta && (
              <ItemListEditor
                sectionType={sectionType}
                items={itemsArray}
                meta={itemMeta.items}
                columnMeta={itemMeta.columns}
                columnHeaders={colHeaders}
                onUpdate={(newItems) => updateProp(itemMeta.items.prop, newItems)}
                onUpdateColumns={
                  itemMeta.columns
                    ? (newHeaders) => updateProp(itemMeta.columns!.prop, newHeaders)
                    : undefined
                }
                focusItemRequest={editor.focusItemRequest}
                onClearFocusRequest={() => editor.setFocusItemRequest(null)}
              />
            )}

            {/* d. Image uploads */}
            {imageProps.map((propName) => {
              const cropMeta = cropMetaPropNames(propName);
              const constraint = getCropConstraint(
                sectionType,
                propName,
                {
                  layout: props.layout as string | undefined,
                  imageAspect: props.imageAspect as string | undefined,
                },
              );
              return (
                <ImageUploadField
                  key={propName}
                  label={
                    propName === "backgroundImageUrl"
                      ? "Background image"
                      : "Image"
                  }
                  value={(props[propName] as string) || undefined}
                  onChange={(url) => {
                    updateProp(propName, url || undefined);
                    // Auto-set overlay + dark bg on first background image upload
                    if (propName === "backgroundImageUrl" && url && !props.backgroundImageUrl) {
                      // Only set defaults if user hasn't explicitly changed them
                      if (props.overlayOpacity === "none" || !props.overlayOpacity) {
                        updateProp("overlayOpacity", "medium");
                      }
                      if (props.backgroundStyle === "surface" || props.backgroundStyle === "light") {
                        updateProp("backgroundStyle", "dark");
                      }
                    }
                  }}
                  cropConstraint={constraint}
                  imageOriginalUrl={props[cropMeta.originalUrl] as string | undefined}
                  imageOriginalStorageKey={props[cropMeta.originalStorageKey] as string | undefined}
                  imageCrop={
                    props[cropMeta.crop] as
                      | { x: number; y: number; width: number; height: number; zoom: number }
                      | undefined
                  }
                  onCropMetadataChange={(meta) => {
                    updateProp(cropMeta.originalUrl, meta.imageOriginalUrl);
                    updateProp(cropMeta.originalStorageKey, meta.imageOriginalStorageKey);
                    updateProp(cropMeta.crop, meta.imageCrop);
                  }}
                />
              );
            })}

            {/* d2. Image alt text */}
            {hasImageAlt && (
              <div className="mb-[18px]">
                <label htmlFor="inspector-image-alt" className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Image alt text
                </label>
                <input
                  id="inspector-image-alt"
                  type="text"
                  value={(props.imageAlt as string) || ""}
                  onChange={(e) => updateProp("imageAlt", e.target.value)}
                  placeholder="Describe the image..."
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e. Link/URL fields (with target toggle) */}
            {hrefProps.map((propName) => {
              const targetProp = propName.replace(/Href$/, "Target");
              const hasTarget = targetProp in props;
              return (
                <LinkField
                  key={propName}
                  label={hrefLabel(propName)}
                  value={(props[propName] as string) || ""}
                  onChange={(v) => updateProp(propName, v)}
                  target={hasTarget ? (props[targetProp] as string) || "_self" : undefined}
                  onTargetChange={hasTarget ? (v) => updateProp(targetProp, v) : undefined}
                />
              );
            })}

            {/* e2a. Show dates (BlogGrid) */}
            {"showDate" in props && isControlVisible(sectionType, currentLayout, "showDate") && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!props.showDate}
                    onChange={(e) => updateProp("showDate", e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show dates
                  </span>
                </label>
              </div>
            )}

            {/* e2b. Max items (FAQ) */}
            {"maxItems" in props && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group mb-2">
                  <input
                    type="checkbox"
                    checked={props.maxItems === null}
                    onChange={(e) =>
                      updateProp("maxItems", e.target.checked ? null : 5)
                    }
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show all FAQs
                  </span>
                </label>
                {props.maxItems !== null && (
                  <div>
                    <label htmlFor="inspector-max-items" className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                      Max items
                    </label>
                    <input
                      id="inspector-max-items"
                      type="number"
                      min={1}
                      value={(props.maxItems as number) || 5}
                      onChange={(e) =>
                        updateProp("maxItems", Math.max(1, Number(e.target.value) || 1))
                      }
                      className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                    />
                  </div>
                )}
              </div>
            )}

            {/* e2c. Show author (BlogGrid) */}
            {"showAuthor" in props && isControlVisible(sectionType, currentLayout, "showAuthor") && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!props.showAuthor}
                    onChange={(e) => updateProp("showAuthor", e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show author
                  </span>
                </label>
              </div>
            )}

            {/* e2d. Show ratings (Testimonials) */}
            {"showRating" in props && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!props.showRating}
                    onChange={(e) => updateProp("showRating", e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show star ratings
                  </span>
                </label>
              </div>
            )}

            {/* e2e. Show CTA (PricingTable) */}
            {"showCta" in props && isControlVisible(sectionType, currentLayout, "showCta") && (
              <div className="mb-[18px]">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={props.showCta !== false}
                    onChange={(e) => updateProp("showCta", e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
                  />
                  <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
                    Show CTA buttons
                  </span>
                </label>
              </div>
            )}

            {/* e2f. Column CTA label (ComparisonTable -- ctaLabel is inline-editable
                in other sections but section-level in ComparisonTable) */}
            {"ctaLabel" in props && "rows" in props && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  CTA button label
                </label>
                <input
                  type="text"
                  value={(props.ctaLabel as string) || ""}
                  onChange={(e) => updateProp("ctaLabel", e.target.value || undefined)}
                  placeholder="e.g. Get Started"
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e2g. Read more label (BlogGrid) */}
            {"readMoreLabel" in props && isControlVisible(sectionType, currentLayout, "readMoreLabel") && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Read more label
                </label>
                <input
                  type="text"
                  value={(props.readMoreLabel as string) || ""}
                  onChange={(e) => updateProp("readMoreLabel", e.target.value || undefined)}
                  placeholder="e.g. Read more..."
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e3a. TextBlock two-column: intro text */}
            {(schemaShape ? "intro" in schemaShape : "intro" in props) && props.layout === "two-column" && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Intro text
                </label>
                <input
                  type="text"
                  value={(props.intro as string) || ""}
                  onChange={(e) => updateProp("intro", e.target.value || undefined)}
                  placeholder="Full-width text above columns..."
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e3b. TextBlock two-column: outro text */}
            {(schemaShape ? "outro" in schemaShape : "outro" in props) && props.layout === "two-column" && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Outro text
                </label>
                <input
                  type="text"
                  value={(props.outro as string) || ""}
                  onChange={(e) => updateProp("outro", e.target.value || undefined)}
                  placeholder="Full-width text below columns..."
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e3c. TextBlock two-column: column card style */}
            {(schemaShape ? "columnCardStyle" in schemaShape : "columnCardStyle" in props) && props.layout === "two-column" && (
              <SegmentedControl
                label="Column style"
                options={[
                  { label: "None", value: "none" },
                  { label: "Elevated", value: "elevated" },
                  { label: "Outlined", value: "outlined" },
                ]}
                value={(props.columnCardStyle as string) || "none"}
                onChange={(v) => updateProp("columnCardStyle", v)}
              />
            )}

            {/* e3d. CTABanner: microcopy text */}
            {(schemaShape ? "microcopy" in schemaShape : "microcopy" in props) && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Microcopy
                </label>
                <input
                  type="text"
                  value={(props.microcopy as string) || ""}
                  onChange={(e) => updateProp("microcopy", e.target.value || undefined)}
                  placeholder="e.g. No retainer - Available 7 days"
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e3e. PricingTable: footnote text */}
            {(schemaShape ? "footnote" in schemaShape : "footnote" in props) && (
              <div className="mb-[18px]">
                <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
                  Footnote
                </label>
                <input
                  type="text"
                  value={(props.footnote as string) || ""}
                  onChange={(e) => updateProp("footnote", e.target.value || undefined)}
                  placeholder="e.g. Here's how it works..."
                  className="w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
                />
              </div>
            )}

            {/* e2. Heading level + Dividers + Hide on mobile (details group) */}
            <SectionSettings
              group="details"
              props={props}
              updateProp={updateProp}
              showHeadingLevel={showHeadingLevel}
            />

            {/* f. Section actions */}
            <SectionActions
              editor={editor}
              sectionId={sectionId}
              isHidden={isHidden}
            />
          </>
        ) : (
          <div className="flex flex-col items-center text-center px-4 pt-8">
            <div className="w-10 h-10 rounded-xl bg-paper-2 grid place-items-center mb-3">
              <MousePointerClick size={18} strokeWidth={1.8} className="text-ink-4" />
            </div>
            <p className="text-[13px] font-medium text-ink-3 mb-1">
              No section selected
            </p>
            <p className="text-[12px] text-ink-5 leading-relaxed mb-4">
              Select a section on the page to edit it, or add a new one.
            </p>
            <button
              onClick={onOpenLibrary}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-tide hover:text-tide-d transition-colors border-0 bg-transparent cursor-pointer"
            >
              <Plus size={14} strokeWidth={2.5} />
              Add section
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
